import React, { useState, useEffect } from 'react';

interface GameState {
  currentGame: 'memory' | 'math' | 'pattern' | 'word';
  score: number;
  level: number;
  isPlaying: boolean;
  timeLeft: number;
}

interface MemoryCard {
  id: number;
  emoji: string;
  isFlipped: boolean;
  isMatched: boolean;
}

const InteractiveBrainGames: React.FC = () => {
  const [gameState, setGameState] = useState<GameState>({
    currentGame: 'memory',
    score: 0,
    level: 1,
    isPlaying: false,
    timeLeft: 0
  });

  // Memory Game State
  const [memoryCards, setMemoryCards] = useState<MemoryCard[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  
  // Math Game State
  const [mathProblem, setMathProblem] = useState({ question: '', answer: 0, userAnswer: '' });
  
  // Pattern Game State
  const [pattern, setPattern] = useState<number[]>([]);
  const [userPattern, setUserPattern] = useState<number[]>([]);
  const [isShowingPattern, setIsShowingPattern] = useState(false);

  const gameEmojis = ['🧠', '🎯', '⚡', '🌟', '🔥', '💎', '🚀', '🎮'];

  useEffect(() => {
    if (gameState.isPlaying && gameState.timeLeft > 0) {
      const timer = setTimeout(() => {
        setGameState(prev => ({ ...prev, timeLeft: prev.timeLeft - 1 }));
      }, 1000);
      return () => clearTimeout(timer);
    } else if (gameState.timeLeft === 0 && gameState.isPlaying) {
      endGame();
    }
  }, [gameState.timeLeft, gameState.isPlaying]);

  const startGame = (gameType: 'memory' | 'math' | 'pattern' | 'word') => {
    setGameState(prev => ({
      ...prev,
      currentGame: gameType,
      isPlaying: true,
      timeLeft: 60,
      score: 0,
      level: 1
    }));

    if (gameType === 'memory') {
      initMemoryGame();
    } else if (gameType === 'math') {
      generateMathProblem();
    } else if (gameType === 'pattern') {
      initPatternGame();
    }
  };

  const endGame = () => {
    setGameState(prev => ({ ...prev, isPlaying: false, timeLeft: 0 }));
  };

  // Memory Game Logic
  const initMemoryGame = () => {
    const cardEmojis = gameEmojis.slice(0, 4);
    const shuffledCards = [...cardEmojis, ...cardEmojis]
      .sort(() => Math.random() - 0.5)
      .map((emoji, index) => ({
        id: index,
        emoji,
        isFlipped: false,
        isMatched: false
      }));
    setMemoryCards(shuffledCards);
    setFlippedCards([]);
  };

  const flipCard = (cardId: number) => {
    if (flippedCards.length === 2 || memoryCards[cardId].isMatched) return;

    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    const newCards = memoryCards.map(card =>
      card.id === cardId ? { ...card, isFlipped: true } : card
    );
    setMemoryCards(newCards);

    if (newFlippedCards.length === 2) {
      const [first, second] = newFlippedCards;
      if (memoryCards[first].emoji === memoryCards[second].emoji) {
        // Match found
        setTimeout(() => {
          setMemoryCards(prev => prev.map(card =>
            card.id === first || card.id === second
              ? { ...card, isMatched: true }
              : card
          ));
          setFlippedCards([]);
          setGameState(prev => ({ ...prev, score: prev.score + 10 }));
        }, 500);
      } else {
        // No match
        setTimeout(() => {
          setMemoryCards(prev => prev.map(card =>
            card.id === first || card.id === second
              ? { ...card, isFlipped: false }
              : card
          ));
          setFlippedCards([]);
        }, 1000);
      }
    }
  };

  // Math Game Logic
  const generateMathProblem = () => {
    const a = Math.floor(Math.random() * 20) + 1;
    const b = Math.floor(Math.random() * 20) + 1;
    const operations = ['+', '-', '×'];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    
    let answer = 0;
    let question = '';
    
    switch (operation) {
      case '+':
        answer = a + b;
        question = `${a} + ${b}`;
        break;
      case '-':
        answer = a - b;
        question = `${a} - ${b}`;
        break;
      case '×':
        answer = a * b;
        question = `${a} × ${b}`;
        break;
    }
    
    setMathProblem({ question, answer, userAnswer: '' });
  };

  const checkMathAnswer = () => {
    if (parseInt(mathProblem.userAnswer) === mathProblem.answer) {
      setGameState(prev => ({ ...prev, score: prev.score + 5 }));
      generateMathProblem();
    }
    setMathProblem(prev => ({ ...prev, userAnswer: '' }));
  };

  // Pattern Game Logic
  const initPatternGame = () => {
    const newPattern = Array.from({ length: 3 }, () => Math.floor(Math.random() * 4));
    setPattern(newPattern);
    setUserPattern([]);
    setIsShowingPattern(true);
    
    setTimeout(() => {
      setIsShowingPattern(false);
    }, 2000);
  };

  const addToUserPattern = (number: number) => {
    const newUserPattern = [...userPattern, number];
    setUserPattern(newUserPattern);
    
    if (newUserPattern.length === pattern.length) {
      if (JSON.stringify(newUserPattern) === JSON.stringify(pattern)) {
        setGameState(prev => ({ ...prev, score: prev.score + 15 }));
        setTimeout(() => {
          initPatternGame();
        }, 1000);
      } else {
        setTimeout(() => {
          setUserPattern([]);
        }, 1000);
      }
    }
  };

  const renderCurrentGame = () => {
    switch (gameState.currentGame) {
      case 'memory':
        return (
          <div className="row g-2">
            {memoryCards.map((card) => (
              <div key={card.id} className="col-3">
                <div
                  className={`card border-0 shadow-sm h-100 ${
                    card.isFlipped || card.isMatched ? '' : 'bg-primary'
                  }`}
                  style={{
                    borderRadius: '15px',
                    cursor: 'pointer',
                    minHeight: '80px',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => flipCard(card.id)}
                >
                  <div className="card-body d-flex align-items-center justify-content-center p-2">
                    <span style={{ fontSize: '24px' }}>
                      {card.isFlipped || card.isMatched ? card.emoji : '?'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'math':
        return (
          <div className="text-center">
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
              <div className="card-body p-4">
                <h2 className="fw-bold mb-3" style={{ fontSize: '2.5rem' }}>
                  {mathProblem.question} = ?
                </h2>
                <div className="d-flex gap-2 justify-content-center">
                  <input
                    type="number"
                    className="form-control text-center"
                    style={{ maxWidth: '100px', borderRadius: '10px' }}
                    value={mathProblem.userAnswer}
                    onChange={(e) => setMathProblem(prev => ({ ...prev, userAnswer: e.target.value }))}
                    onKeyPress={(e) => e.key === 'Enter' && checkMathAnswer()}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={checkMathAnswer}
                    style={{ borderRadius: '10px' }}
                  >
                    Check
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'pattern':
        return (
          <div className="text-center">
            <p className="text-muted mb-3">
              {isShowingPattern ? 'Memorize the pattern!' : 'Repeat the pattern:'}
            </p>
            <div className="row g-2 justify-content-center">
              {[0, 1, 2, 3].map((number) => (
                <div key={number} className="col-3">
                  <button
                    className={`btn w-100 ${
                      isShowingPattern && pattern.includes(number)
                        ? 'btn-warning'
                        : userPattern.includes(number)
                        ? 'btn-success'
                        : 'btn-outline-primary'
                    }`}
                    style={{ 
                      borderRadius: '15px',
                      height: '60px',
                      fontSize: '20px'
                    }}
                    onClick={() => !isShowingPattern && addToUserPattern(number)}
                    disabled={isShowingPattern}
                  >
                    {number + 1}
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return <div>Select a game to start!</div>;
    }
  };

  return (
    <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
      <div className="card-header bg-transparent border-0 p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">
            <i className="fas fa-gamepad me-2 text-primary"></i>
            Brain Training Games
          </h5>
          {gameState.isPlaying && (
            <div className="d-flex gap-3">
              <span className="badge bg-primary rounded-pill">
                Score: {gameState.score}
              </span>
              <span className="badge bg-warning rounded-pill">
                Time: {gameState.timeLeft}s
              </span>
            </div>
          )}
        </div>
        
        {!gameState.isPlaying && (
          <div className="row g-2">
            <div className="col-6 col-lg-3">
              <button
                className="btn btn-outline-primary w-100"
                onClick={() => startGame('memory')}
                style={{ borderRadius: '15px' }}
              >
                <i className="fas fa-brain d-block mb-1"></i>
                Memory
              </button>
            </div>
            <div className="col-6 col-lg-3">
              <button
                className="btn btn-outline-success w-100"
                onClick={() => startGame('math')}
                style={{ borderRadius: '15px' }}
              >
                <i className="fas fa-calculator d-block mb-1"></i>
                Math
              </button>
            </div>
            <div className="col-6 col-lg-3">
              <button
                className="btn btn-outline-warning w-100"
                onClick={() => startGame('pattern')}
                style={{ borderRadius: '15px' }}
              >
                <i className="fas fa-th d-block mb-1"></i>
                Pattern
              </button>
            </div>
            <div className="col-6 col-lg-3">
              <button
                className="btn btn-outline-info w-100"
                disabled
                style={{ borderRadius: '15px' }}
              >
                <i className="fas fa-spell-check d-block mb-1"></i>
                Words
                <small className="d-block">Soon</small>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card-body p-4 pt-0">
        {gameState.isPlaying ? (
          <div>
            {renderCurrentGame()}
            <div className="text-center mt-3">
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={endGame}
                style={{ borderRadius: '15px' }}
              >
                End Game
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <i className="fas fa-play-circle fa-3x text-muted mb-3"></i>
            <h6 className="text-muted">Choose a game to train your brain!</h6>
            <p className="text-muted small">
              These mini-games will help prepare your mind for AI-powered learning
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveBrainGames;